/** @format */

import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';
import { getTime, startOfDay } from 'date-fns';
import { debounce, get, isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useCreateBookingRequestMutation, useRetractBookingRequestMutation } from 'src/api/booking-request-api';
import { DialogTitleWithButtonClose } from 'src/components/modals';
import { refreshKeyConfig } from 'src/config';
import { useBookingRequestAction } from 'src/hooks/use-booking-request-action';
import { useServerError } from 'src/hooks/use-server-error';
import { setRefesh } from 'src/store/app-slice';
import { isDate } from 'src/utils/datetime';
import * as Yup from 'yup';
import { clearOwnedSelect, selectResultConditionListSendTo, unsetAssignedCar } from '../../slices/booking-slice';
import { ResultOwnedCarAssign, ResultOwnedSetBaseFee } from './booking-result-item';

const validationRequestSchema = Yup.object({
  message: Yup.string().required('Vui lòng điền thông tin lộ trình.')
});

const defaultRequestValues = {
  message: '8h 5-6/12: q10 -> vũng tàu (15h khởi hành về) \n16c (solati): 3.3tr (tx ăn nghỉ tự túc)'
};

export const reduceBaseFeeConditions = (conditions) => {
  return conditions.reduce((acc, user) => {
    const uniqueBaseFees = {};

    user.cars.forEach((car) => {
      const { id, name } = car.baseFee;
      if (!uniqueBaseFees[id]) {
        uniqueBaseFees[id] = { value: name, checked: true }; // Tạo cấu trúc object
      }
    });

    acc[user.id] = uniqueBaseFees; // Lưu trực tiếp uniqueBaseFees làm giá trị
    return acc;
  }, {});
};

const normalizeRequestCars = (requestCars) => {
  const updatedRequestCars = {};

  Object.entries(requestCars).forEach(([key, value]) => {
    const { baseFeeId, baseFeeIds = [], ...rest } = value;

    // Nếu `baseFeeId` tồn tại, thêm nó vào `baseFeeIds` (đảm bảo không trùng lặp)
    const updatedBaseFeeIds = baseFeeId ? Array.from(new Set([...baseFeeIds, baseFeeId])) : baseFeeIds;

    // Tạo đối tượng mới với `baseFeeIds` và không có `baseFeeId`
    updatedRequestCars[key] = {
      ...rest,
      baseFeeId,
      baseFeeIds: updatedBaseFeeIds
    };
  });

  return updatedRequestCars;
};
/**
 * RequestNhaxeFormModal
 *
 */
export function RequestNhaxeFormModal({ open, handleClose, conditionId }) {
  const dispatch = useDispatch();
  const { processError } = useServerError();

  const [createBookingRequest] = useCreateBookingRequestMutation();

  const resultConditions = useSelector(selectResultConditionListSendTo);
  console.log('resultConditions', resultConditions);

  const [formSubmiting, setFormSubmiting] = useState(false);
  const [selectedForNhaxe, setSelectedForNhaxe] = useState({});

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultRequestValues,
    resolver: yupResolver(validationRequestSchema)
  });

  const onToggleBaseFee = useCallback((ownedId, baseFeeId, carId) => {
    setSelectedForNhaxe((prev) => {
      // Sao chép trạng thái hiện tại để không thay đổi trực tiếp
      const selected = { ...prev };
      const selectedOwned = get(selected, ownedId, {});
      const selectedBaseFeeIds = get(selectedOwned, 'baseFeeIds', []);
      const selectedCarId = get(selectedOwned, 'carId');

      // Kiểm tra nếu `baseFeeId` đã tồn tại trong `baseFeeIds`
      const isBaseFeeSelected = selectedBaseFeeIds.includes(baseFeeId);
      console.log('onToggleBaseFee isBaseFeeSelected', isBaseFeeSelected);
      selected[ownedId] = {
        ...selectedOwned,
        ...(!selectedCarId && {
          carId: carId,
          baseFeeId
        }),
        baseFeeIds: isBaseFeeSelected
          ? selectedBaseFeeIds.filter((id) => id !== baseFeeId) // Loại bỏ baseFeeId nếu đã tồn tại
          : [...selectedBaseFeeIds, baseFeeId] // Thêm baseFeeId nếu chưa tồn tại
      };

      return selected; // Trả về trạng thái mới
    });
  }, []);

  const onSetCarsCheck = useCallback((ownedId, carId, baseFeeId) => {
    setSelectedForNhaxe((prev) => {
      const selected = { ...prev };
      const selectedOwned = get(selected, ownedId, {});

      // const baseFeeIds = get(updated, `${ownedId}.baseFeeIds`, []);
      // Cập nhật giá trị checked
      selected[ownedId] = {
        ...selectedOwned,
        carId: carId,
        baseFeeId: baseFeeId // Loại bỏ phần tử trùng
      };

      return selected;
    });
  }, []);

  const debounceSubmitSend = useCallback(
    debounce(async (values) => {
      try {
        console.log('resultConditions debounceSubmitSend', values);
        await createBookingRequest({ ...values, conditionId }).unwrap();

        toast.success(`Yêu cầu đã được gửi đến nhà xe`);
        dispatch(clearOwnedSelect());
        handleClose();
      } catch (error) {
        processError(error);
      } finally {
        dispatch(
          setRefesh({
            [refreshKeyConfig.pageLoading]: false
          })
        );
        setFormSubmiting(false);
      }
    }, 200),
    [conditionId]
  );

  const onSubmit = async (values) => {
    // const sendToBaseFeeId = reduceBaseFeeSendTo(carForNhaxe);
    if (isEmpty(selectedForNhaxe)) {
      toast.error('Vui lòng chọn xe muốn gửi kèo.');
    } else {
      setFormSubmiting(true);
      dispatch(
        setRefesh({
          [refreshKeyConfig.pageLoading]: getTime(new Date())
        })
      );
      await debounceSubmitSend({ ...values, requestCars: normalizeRequestCars(selectedForNhaxe) });
    }
  };
  console.log('onToggleBaseFee selectedForNhaxe', selectedForNhaxe);
  useEffect(() => {
    if (open) {
      reset(defaultRequestValues);
    } else {
      setSelectedForNhaxe({});
    }
  }, [open]);
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitleWithButtonClose title={'Gửi thông tin kèo tìm xe'} onClose={handleClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack rowGap={2}>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  margin="dense"
                  variant="standard"
                  multiline
                  rows={4}
                  fullWidth
                  InputProps={{
                    inputProps: {
                      style: { resize: 'both' } // Thiết lập cho textarea
                    }
                  }}
                  label="Nội dung gửi"
                  error={!!errors.message}
                  helperText={errors?.message?.message}
                />
              )}
            />
          </Stack>
          <Stack
            divider={<Divider />}
            rowGap={1}
            sx={{
              borderBottomColor: 'divider',
              borderBottomStyle: 'solid',
              borderBottomWidth: 1,
              // listStyle: 'none',
              m: 0,
              my: 1,
              p: 0,
              pb: 1
            }}
          >
            {resultConditions.map((result) => {
              return (
                <ResultOwnedSetBaseFee
                  key={result.id}
                  result={result}
                  selectedForNhaxe={selectedForNhaxe}
                  onToggleBaseFee={onToggleBaseFee}
                  onSetCarsCheck={onSetCarsCheck}
                />
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={formSubmiting || isEmpty(resultConditions)}>
            Gửi
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const defautlConfirmValues = {
  start: '',
  end: '',
  message:
    'XÁC NHẬN GIAO XE\nMã xe: NX35752-1\nKhách hàng: A. Bình (Brian) (0385411575)\n-----------------\nThời gian: 02:00 17/12 (khởi hành đi) → 10:00 19/12 (khởi hành về)\nLộ trình: 259 Điện Biên Phủ Q3 → Đà Lạt\nLoại tour: 2 chiều\nLoại xe: Giường nằm (40 Giường)\nTài xế tự túc\n-----------------\nGiá xe: 14.000.000\nThu khách: 14.000.000\nCông nợ: 0'
};

const validationAssignSchema = Yup.object({
  message: Yup.string().required('Vui lòng điền thông tin lộ trình.'),
  start: Yup.date().required('Thời gian khởi hành đi là bắt buộc.').typeError('Thời gian khởi hành đi là bắt buộc.'),
  end: Yup.date().required('Thời gian khởi hành về là bắt buộc.').typeError('Thời gian khởi hành về là bắt buộc.')
});

export function AssignNhaxeFormModal({ open, handleClose, ownerName, assigned, requestId, conditionId, start, end, requestCar = {} }) {
  const dispatch = useDispatch();
  console.log('requestCar', requestCar);

  const [formSubmiting, setFormSubmiting] = useState(false);

  const { handleAssign } = useBookingRequestAction();
  // const defaultValues = useMemo(() => ({
  //   message: 'XÁC NHẬN GIAO XE\nMã xe: NX35752-1\nKhách hàng: A. B…á xe: 14.000.000\nThu khách: 14.000.000\nCông nợ: 0',
  //   start,
  //   end,
  // }), [start, end]);

  const carId = get(requestCar, 'id');
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: defautlConfirmValues,
    resolver: yupResolver(validationAssignSchema)
  });

  const debounceSubmitSend = useCallback(
    debounce(async ({ start, end, message }) => {
      try {
        await handleAssign({ id: requestId, message, start, end, assigned });
        dispatch(unsetAssignedCar({ conditionId, carId, ownedId: assigned }));

        handleClose();
      } finally {
        setFormSubmiting(false);
      }
    }, 200),
    [assigned, carId, requestId, conditionId, start, end]
  );

  const onSubmit = async (values) => {
    setFormSubmiting(true);
    dispatch(
      setRefesh({
        [refreshKeyConfig.pageLoading]: getTime(new Date())
      })
    );

    await debounceSubmitSend(values);
  };

  useEffect(() => {
    if (open) {
      // console.log('defaultValues', { ...defautlConfirmValues, start: parseISO(start), end });

      reset({ ...defautlConfirmValues, start: isDate(start) ? startOfDay(start) : '', end: isDate(end) ? startOfDay(end) : '' });
    }
  }, [open, start, end]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitleWithButtonClose title={`Xác nhận kèo với nhà xe `} onClose={handleClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ minWidth: 320 }}>
          <Stack rowGap={2}>
            <Stack direction="row" justifyContent="space-around">
              <Controller
                name="start"
                control={control}
                render={({ field: { onChange, value } }) => {
                  // const valueStart = new Date(value);
                  return (
                    <>
                      <DateTimePicker
                        value={new Date(value)}
                        format="dd/MM/yyyy HH:mm"
                        label="Khởi hành đi"
                        ampm={false} // Hiển thị 24 giờ
                        // shouldDisableDate={(day) => isDateBlocked(day, blockedDates, { currentRangeDates, canStartInDate })}
                        // slots={{
                        //   day: BlockedDayPicker // Chỉ định sử dụng `Day` component để render các ngày
                        // }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            error: !!errors?.start,
                            InputLabelProps: { shrink: true }
                          }
                          // day: {
                          //   blockedDates,
                          //   currentRangeDates,
                          //   canStartInDate
                          // }
                        }}
                        // onChange={(newValue) => {
                        //   onChange(getTime(new Date(newValue)));
                        // }}
                        sx={{ width: '200px' }}
                        onChange={onChange}
                      />
                      {/* <TimePicker
                        value={value}
                        // format="dd/MM/yyyy"
                        label={`Giờ đón (${formatDateUTC(start, 'dd/MM/yyyy')})`}
                        onChange={onChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            error: !!errors?.timeStart,
                            InputLabelProps: { shrink: true }
                          }
                        }}
                        sx={{ width: '120px' }}
                      /> */}
                    </>
                  );
                }}
              />
              <Controller
                name="end"
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => {
                  // const valueEnd = new Date(value);
                  return (
                    <>
                      <DateTimePicker
                        value={new Date(value)}
                        format="dd/MM/yyyy HH:mm"
                        label="Ngày giờ khởi hành về"
                        ampm={false} // Hiển thị 24 giờ
                        // shouldDisableDate={(day) => isDateBlocked(day, blockedDates, { currentRangeDates, canStartInDate })}
                        // slots={{
                        //   day: BlockedDayPicker // Chỉ định sử dụng `Day` component để render các ngày
                        // }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            error: !!errors?.end,
                            InputLabelProps: { shrink: true }
                          }
                          // day: {
                          //   blockedDates,
                          //   currentRangeDates,
                          //   canStartInDate
                          // }
                        }}
                        // onChange={(newValue) => {
                        //   onChange(getTime(new Date(newValue)));
                        // }}
                        sx={{ width: '200px' }}
                        onChange={onChange}
                      />
                      {/* <TimePicker
                        value={value}
                        // format="dd/MM/yyyy"
                        label={`Giờ về (${formatDateUTC(end, 'dd/MM/yyyy')})`}
                        onChange={onChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            variant: 'standard',
                            error: !!errors?.timeEnd,
                            InputLabelProps: { shrink: true }
                          }
                        }}
                        sx={{ width: '120px' }}
                        error={false}
                      /> */}
                    </>
                  );
                }}
              />
            </Stack>
            {!isEmpty(requestCar) && (
              <>
                <ResultOwnedCarAssign displayName={ownerName} car={requestCar} />
                {/* <Typography variant="h6" color="primary.main">
                  {ownerName}
                </Typography>
                <Stack direction="row" columnGap={0.5} width="100%" alignItems="baseline">
                  <div>
                    <Typography variant="subtitle2">{requestCar?.model?.name}</Typography>
                    <LicensePlate>{requestCar.licensePlate}</LicensePlate>
                  </div>
                </Stack> */}
              </>
            )}

            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  margin="dense"
                  variant="standard"
                  multiline
                  // maxRows={20}
                  fullWidth
                  InputProps={{
                    inputProps: {
                      style: { resize: 'both', paddingBottom: '10px' } // Thiết lập cho textarea
                    }
                  }}
                  label="Nội dung xác nhận"
                  error={!!errors.message}
                  helperText={errors?.message?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={formSubmiting}>
            Gửi
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const defautlRetractValues = {
  messageRetract: ''
};
export function RetractNhaxeFormModal({ open, handleClose, requestId }) {
  const dispatch = useDispatch();
  const { processError } = useServerError();

  const [formSubmiting, setFormSubmiting] = useState(false);
  const [retractBookingReques] = useRetractBookingRequestMutation();

  const {
    control,

    handleSubmit,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: defautlRetractValues
  });

  const debounceSubmitRetract = useCallback(
    debounce(async (values) => {
      try {
        await retractBookingReques({ id: requestId, ...values }).unwrap();

        toast.success(`Kèo đã được thu hồi`);

        handleClose();
      } catch (error) {
        processError(error);
      } finally {
        dispatch(
          setRefesh({
            [refreshKeyConfig.pageLoading]: false
          })
        );
        setFormSubmiting(false);
      }
    }, 200),
    [requestId]
  );

  const onSubmit = async (values) => {
    setFormSubmiting(true);
    dispatch(
      setRefesh({
        [refreshKeyConfig.pageLoading]: getTime(new Date())
      })
    );

    await debounceSubmitRetract(values);
  };

  useEffect(() => {
    if (open) {
      reset(defautlRetractValues);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitleWithButtonClose title={'Thu hồi'} onClose={handleClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ minWidth: 280 }}>
          <Stack rowGap={2}>
            <Controller
              name="messageRetract"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  margin="dense"
                  variant="standard"
                  multiline
                  minRows={4}
                  fullWidth
                  InputProps={{
                    inputProps: {
                      style: { resize: 'both', paddingBottom: '10px' } // Thiết lập cho textarea
                    }
                  }}
                  label="Lý do thu hồi"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={formSubmiting}>
            Thu hồi{' '}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
